import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { catchError, retry, timeout } from 'rxjs/operators';
import {
  ApiResponseDTO,
  environment,
  getAuthHeaders,
  handleError,
} from '@sandbox-platform/shared-ui';
import {
  AttendanceAutoCloseLogDTO,
  AttendanceSettingsDTO,
  CreateAttendanceSettingsDTO,
  UpdateAttendanceSettingsDTO,
} from './attendance-settings.types';

@Injectable({
  providedIn: 'root',
})
export class AttendanceSettingsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.expertApiUrl}`;
  private readonly settingsUrl = `${this.baseUrl}/attendance/settings`;
  private readonly defaultTimeout = 10000;
  private readonly retryAttempts = 2;

  get(): Observable<ApiResponseDTO<AttendanceSettingsDTO>> {
    return this.http
      .get<ApiResponseDTO<AttendanceSettingsDTO>>(this.settingsUrl, {
        headers: getAuthHeaders(),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(handleError)
      );
  }

  create(
    dto: CreateAttendanceSettingsDTO
  ): Observable<ApiResponseDTO<AttendanceSettingsDTO>> {
    return this.http
      .post<ApiResponseDTO<AttendanceSettingsDTO>>(this.settingsUrl, dto, {
        headers: getAuthHeaders(),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(handleError)
      );
  }

  update(
    dto: UpdateAttendanceSettingsDTO
  ): Observable<ApiResponseDTO<AttendanceSettingsDTO>> {
    return this.http
      .put<ApiResponseDTO<AttendanceSettingsDTO>>(this.settingsUrl, dto, {
        headers: getAuthHeaders(),
      })
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(handleError)
      );
  }

  getLogs(days = 15): Observable<ApiResponseDTO<AttendanceAutoCloseLogDTO[]>> {
    return this.http
      .get<ApiResponseDTO<AttendanceAutoCloseLogDTO[]>>(
        `${this.settingsUrl}/auto-close-logs`,
        {
          headers: getAuthHeaders(),
          params: { days: days.toString() },
        }
      )
      .pipe(
        timeout(this.defaultTimeout),
        retry(this.retryAttempts),
        catchError(handleError)
      );
  }
}
